/**
 * Quotes (spec §9.1, plan §6 M2-07): the single price entry point of M2. Trade commands, the best-price hint, the
 * encounter director's cargo estimate and the Market screen all price through `quote()`, so M4 can swap the
 * static-with-drift model for stock-based prices (simulation spec §3.3) behind this one function.
 *
 *   sell unit = base × stationMult × drift(t) × satMult(s) × (1 + swing·standing/100)
 *   buy unit  = base × stationMult × drift(t) × satMult(s) × buyMarkup × (1 − swing·standing/100)
 *
 * `base` is the ware's `basePrice` per tonne, or a component's `value` per piece. A quantity moves the saturation
 * as it trades (a sale raises `s` by `qty / capacity`, a purchase lowers it to a floor of 0), and the total is the
 * exact integral of the unit price over that move, so dumping 20 t costs what 20 one-tonne sales would.
 * Components move saturation by their mass in tonnes (every station has one `capacityT`).
 *
 * **M4 (plan I3, spec §5.6-§5.7, seam S-M2-2):** when the Reach's strategic economy runs, the career runtime installs
 * a `WareQuoteSource` (`setWareQuoteSource`). Ware quotes then come from the station's real stock
 * (`StrategicMarket.quote`; `satBefore`/`satAfter` carry the fill before and after), and components keep this
 * formula without drift or saturation. A station the source does not know (sandbox, no worker, a station missing
 * from the strategic views) keeps the formula above as the stand-in.
 */
import { stationIndexOf } from '../data/m2'
import { componentTypeIndex, wareIndex, type ComponentType, type M2Data, type StationDef, type StationId, type WareId } from '../data/m2Schemas'
import type { CargoComponent } from '../career/types'
import { marketKey, type MarketState } from '../space/contracts'
import { componentCarriesStory } from '../story/items'
import { drift } from './drift'
import { meanSatMult, satMult, saturationOf } from './market'

/** What is being priced. `dataCore` without a component prices a pristine core (the Market screen's list row). */
export type QuoteItem =
  | { kind: 'ware'; ware: WareId }
  | { kind: 'component'; component: CargoComponent }
  | { kind: 'dataCore'; component?: CargoComponent }

export type TradeSide = 'sell' | 'buy'

export interface Quote {
  /** Credits per unit (tonne or piece) at the current saturation: the marginal price of the next unit. */
  unit: number
  /** Whole credits for the whole quantity, saturation integrated over the trade. */
  total: number
  /** Saturation now (decayed to `t`). */
  satBefore: number
  /** Saturation after this trade. */
  satAfter: number
  /**
   * PT-1/PT-2 (D-803): the units this quote covers when the source clamps the quantity (a strategic station's room on
   * a sale, its stock on a buy). Absent under M2's formula, which has no room limit.
   */
  qty?: number
}

/**
 * The strategic ware prices behind `quote()` (M4 plan I3). `active` is true while the strategic economy runs; then
 * components are priced without drift and saturation. `quoteWare` returns undefined for a station it does not
 * know (the M2 formula prices it), null when that station does not trade the ware on `side`.
 */
export interface WareQuoteSource {
  readonly active: boolean
  quoteWare(stationId: StationId, ware: WareId, qty: number, side: TradeSide, standing: number): Quote | null | undefined
}

let wareSource: WareQuoteSource | null = null

/** Installs (or with null removes) the strategic ware prices. Returns a function that restores the previous source. */
export function setWareQuoteSource(source: WareQuoteSource | null): () => void {
  const previous = wareSource
  wareSource = source
  return () => {
    wareSource = previous
  }
}

/** True while the strategic economy prices wares (M2's saturation is then no longer written). */
export function strategicPricesActive(): boolean {
  return wareSource?.active === true
}

/** A salvaged component's value at `condition` (0..1): `baseValue × (0.3 + 0.7 × condition)` (spec §6.2). */
export function componentValue(data: Pick<M2Data, 'wares'>, type: ComponentType, condition: number): number {
  const c = condition < 0 ? 0 : condition > 1 ? 1 : condition
  return data.wares.components[componentTypeIndex(type)].baseValue * (0.3 + 0.7 * c)
}

function clampStanding(standing: number): number {
  return standing < -100 ? -100 : standing > 100 ? 100 : standing
}

/** Sell-price standing factor: `1 + swing × standing / 100` (±5% at ±100 with the shipped swing). */
export function standingSellMult(standing: number, swing: number): number {
  return 1 + (swing * clampStanding(standing)) / 100
}

/** Buy-price standing factor: `1 − swing × standing / 100`. */
export function standingBuyMult(standing: number, swing: number): number {
  return 1 - (swing * clampStanding(standing)) / 100
}

/** The market key an item's saturation is kept under (`<ware>` or `component:<type>`; data cores are components). */
export function quoteKey(item: QuoteItem): string {
  if (item.kind === 'ware') return marketKey(item.ware)
  if (item.kind === 'dataCore') return marketKey({ component: 'dataCore' })
  return marketKey({ component: item.component.type })
}

interface Priced {
  /** Station multiplier × base price or value. */
  base: number
  /** Saturation units (tonnes) per quantity unit. */
  satPerUnit: number
}

/** The station-multiplied base and saturation weight of `item` at `station`, or null when it is not traded on `side`. */
function priceItem(data: M2Data, station: StationDef, item: QuoteItem, side: TradeSide): Priced | null {
  if (item.kind === 'ware') {
    const mult = station.prices.wares[item.ware]
    if (mult === undefined) return null
    if (side === 'buy' && !station.sells.includes(item.ware)) return null
    return { base: data.wares.wares[wareIndex(item.ware)].basePrice * mult, satPerUnit: 1 }
  }
  // Stations buy salvage; nobody sells it to the player in M2.
  if (side === 'buy') return null
  const component = item.component
  if (component === undefined) {
    // A pristine data core (the Market screen's reference row).
    const mult = station.prices.dataCores
    if (mult === null) return null
    const def = data.wares.components[componentTypeIndex('dataCore')]
    return { base: componentValue(data, 'dataCore', 1) * mult, satPerUnit: def.massT[1] }
  }
  if (component.sealed === true) return null
  // M7 (spec §2.8, EX-1): a story item, or a pod carrying one, has no quote of any kind (not even as a data core).
  if (componentCarriesStory(component)) return null
  const mult = component.type === 'dataCore' ? station.prices.dataCores : station.prices.components
  if (mult === null) return null
  return { base: component.value * mult, satPerUnit: component.massT }
}

/**
 * The price of `qty` units (tonnes of a ware, pieces of a component) of `item` at `stationId` on `side` at game
 * time `t`, with the player's `standing` toward the station's faction. `null` when the station does not trade the
 * item that way (no market, not priced, not sold here, sealed contract cargo, a story item or a pod carrying one).
 * `qty` 0 gives the unit price.
 */
export function quote(
  data: M2Data,
  market: MarketState,
  stationId: StationId,
  item: QuoteItem,
  qty: number,
  side: TradeSide,
  t: number,
  standing: number,
): Quote | null {
  if (!(qty >= 0) || !Number.isFinite(qty)) throw new RangeError(`quote: quantity ${qty} is not a finite number ≥ 0`)
  const index = stationIndexOf(data, stationId)
  if (index < 0) return null
  const station = data.stations.stations[index]
  if (!station.services.includes('market')) return null
  const strategic = strategicPricesActive()
  if (strategic && item.kind === 'ware' && wareSource !== null) {
    const q = wareSource.quoteWare(stationId, item.ware, qty, side, standing)
    if (q !== undefined) return q
  }
  const priced = priceItem(data, station, item, side)
  if (priced === null) return null

  const cfg = data.wares.market
  const k = cfg.saturation.k
  const key = quoteKey(item)
  const s0 = saturationOf(market, stationId, key, t, cfg.saturation.halfLifeSeconds)
  const ds = (qty * priced.satPerUnit) / station.capacityT
  const sideMult =
    side === 'sell' ? standingSellMult(standing, cfg.standingSwing) : cfg.buyMarkup * standingBuyMult(standing, cfg.standingSwing)
  if (strategic && item.kind !== 'ware') {
    // Spec §5.7: under the strategic economy, components keep the formula without drift or saturation.
    return { unit: priced.base * sideMult, total: Math.round(priced.base * sideMult * qty), satBefore: 0, satAfter: 0 }
  }
  const flat = priced.base * drift(stationId, key, t, cfg.drift) * sideMult

  let s1: number
  let mean: number
  if (side === 'sell') {
    s1 = s0 + ds
    mean = meanSatMult(s0, s1, k)
  } else {
    // Buying drains saturation to its floor; past the floor the price stays at satMult(0) = 1.
    const used = ds < s0 ? ds : s0
    s1 = s0 - used
    mean = ds > 0 ? (meanSatMult(s0, s1, k) * used + (ds - used)) / ds : satMult(s0, k)
  }
  return { unit: flat * satMult(s0, k), total: Math.round(flat * mean * qty), satBefore: s0, satAfter: s1 }
}
